import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, BookOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminSessionBanner } from '@/components/layout/AdminSessionBanner';

const nav = [
  { to: '/app/platform/tenants', label: 'Tenants', icon: Building2 },
  { to: '/app/platform/knowledge', label: 'House knowledge', icon: BookOpen },
];

export function PlatformLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [isOrgAdmin, setIsOrgAdmin] = useState<boolean>(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('pulse_org_members')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      if (!cancelled) setIsOrgAdmin(!!data);
    })();
    return () => { cancelled = true; };
  }, [user]);

  const backTarget = isOrgAdmin ? '/app/admin' : '/app/employee';
  const backLabel = isOrgAdmin ? 'Back to admin' : 'Back to my org';

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <AdminSessionBanner />
      <header className="border-b border-border bg-card">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3">
          <div className="font-heading text-sm sm:text-base font-semibold">
            CultureLens — Platform Console
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground truncate max-w-[50%]">
            {user?.email}
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        <aside className="w-56 border-r border-border bg-card hidden sm:flex flex-col p-3 gap-1">
          <nav className="flex flex-col gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                    isActive
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto pt-3 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={() => navigate(backTarget)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {backLabel}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={async () => {
                await signOut();
                navigate('/auth/signin');
              }}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </aside>

        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default PlatformLayout;
